// packages/shared/components/ConfirmDialog.jsx
// Material-style confirm dialog for Govern360. Mirrors Toast.jsx: one module-level store,
// a singleton + hook, and a Provider mounted once at the app root. The singleton returns a
// PROMISE<boolean> so it's a near drop-in for window.confirm():
//   import { confirm } from '@govern360/shared';
//   if (await confirm({ title: 'Remove vendor?', danger: true })) { ...do it... }
// Accessible: role="dialog" aria-modal, Escape cancels, focus moves to the action button,
// backdrop click cancels. Uses --g-* tokens to match the app.
import { useEffect, useState, useRef } from 'react';
import { AlertTriangle } from 'lucide-react';

// ---- module-level store ----------------------------------------------------
let _dialog = null;          // { id, title, message, confirmLabel, cancelLabel, danger, resolve }
let _seq = 0;
const _listeners = new Set();
function _emit() { _listeners.forEach((fn) => fn(_dialog)); }

function _close(result) {
  if (_dialog) {
    const r = _dialog.resolve;
    _dialog = null;
    _emit();
    if (r) r(result);
  }
}

// ---- singleton: returns Promise<boolean> -----------------------------------
export function confirm(opts = {}) {
  return new Promise((resolve) => {
    // if a string is passed, treat it as the message (window.confirm parity)
    const o = (typeof opts === 'string') ? { message: opts } : opts;
    _dialog = {
      id: ++_seq,
      title: o.title || 'Are you sure?',
      message: o.message || '',
      confirmLabel: o.confirmLabel || (o.danger ? 'Delete' : 'Confirm'),
      cancelLabel: o.cancelLabel || 'Cancel',
      danger: !!o.danger,
      resolve,
    };
    _emit();
  });
}

// ---- hook (sugar) ----------------------------------------------------------
export function useConfirm() {
  return { confirm };
}

// ---- provider: renders the active dialog -----------------------------------
export function ConfirmProvider({ children }) {
  const [d, setD] = useState(_dialog);
  const okRef = useRef(null);

  useEffect(() => {
    const fn = (next) => setD(next ? { ...next } : null);
    _listeners.add(fn);
    setD(_dialog ? { ..._dialog } : null);
    return () => { _listeners.delete(fn); };
  }, []);

  useEffect(() => {
    if (!d) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); _close(false); }
      if (e.key === 'Enter') { e.preventDefault(); _close(true); }
    };
    window.addEventListener('keydown', onKey);
    // focus the action button for keyboard users
    const t = setTimeout(() => { if (okRef.current) okRef.current.focus(); }, 0);
    return () => { window.removeEventListener('keydown', onKey); clearTimeout(t); };
  }, [d]);

  return (
    <>
      {children}
      {d && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) _close(false); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(32,33,36,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            role="dialog" aria-modal="true" aria-labelledby="cfm-title"
            style={{
              width: '100%', maxWidth: 420, background: 'var(--g-surface, #fff)',
              borderRadius: 12, boxShadow: 'var(--g-e3, 0 8px 24px rgba(60,64,67,0.18))',
              padding: 24,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
              {d.danger && (
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                  background: 'var(--g-danger-bg, rgba(217,48,37,0.1))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <AlertTriangle size={18} style={{ color: 'var(--g-danger, #d93025)' }} />
                </div>
              )}
              <div style={{ flex: 1 }}>
                <h2 id="cfm-title" style={{
                  margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--g-text, #202124)',
                  fontFamily: 'var(--g-font-display, inherit)',
                }}>{d.title}</h2>
                {d.message && (
                  <p style={{ margin: '8px 0 0', fontSize: 14, lineHeight: 1.5, color: 'var(--g-text-2, #5f6368)' }}>
                    {d.message}
                  </p>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
              <button
                onClick={() => _close(false)}
                style={{
                  height: 36, padding: '0 16px', borderRadius: 8, fontSize: 14, fontWeight: 500,
                  border: '1px solid var(--g-divider, #e8eaed)', background: 'transparent',
                  color: 'var(--g-text, #202124)', cursor: 'pointer',
                }}
              >{d.cancelLabel}</button>
              <button
                ref={okRef}
                onClick={() => _close(true)}
                style={{
                  height: 36, padding: '0 16px', borderRadius: 8, fontSize: 14, fontWeight: 600,
                  border: 'none', cursor: 'pointer', color: '#fff',
                  background: d.danger ? 'var(--g-danger, #d93025)' : 'var(--g-primary, #2e6fb0)',
                }}
              >{d.confirmLabel}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
